import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import i18n from 'i18next';

import {
  errorToastMessage,
  successToastMessage,
} from '../components/layout/toast-root/toast';
import config from '../envConfig';
import { authTokenHolder } from '../shared/auth/auth-token-holder';
import { Messages } from '../shared/messages';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';
import {
  enableMaintenanceMode,
  enableServiceUnavailableMode,
} from './appStatusSlice';
import { HTTP_METHOD, REDUX_CACHE } from './constants';
import { shouldSuppressGlobalErrorToast } from './error-toast-visibility';

/** API error body from backend (statusCode, code, message). */
export type ApiErrorBody = {
  statusCode?: number;
  code?: string;
  message?: string;
};

export type CommonError = {
  message: string;
  status: number;
  /** Backend error code for i18n lookup (e.g. USER_NOT_FOUND). */
  code?: string;
};

export type BaseQueryFnType = BaseQueryFn<
  string | FetchArgs,
  unknown,
  CommonError,
  any,
  FetchBaseQueryMeta
>;

export const TransformedResponseWithNotification = (
  customMessage?: string,
  mergeMessage = true,
  showErrorToastMessage = true,
  shouldSuppressErrorToast?: (error: unknown) => boolean,
) => {
  return {
    transformResponse: (response: any, meta: any): any => {
      let message = '';

      switch (meta.request.method) {
        case HTTP_METHOD.POST:
          message = mergeMessage
            ? `${customMessage} ${Messages.dataCreated}`
            : customMessage || `Data ${Messages.dataCreated}`;
          break;
        case HTTP_METHOD.PUT:
          message = mergeMessage
            ? `${customMessage} ${Messages.dataUpdated}`
            : customMessage || `Data ${Messages.dataUpdated}`;
          break;
        case HTTP_METHOD.DELETE:
          message = mergeMessage
            ? `${customMessage} ${Messages.dataDeleted}`
            : customMessage || `Data ${Messages.dataDeleted}`;
          break;
      }

      successToastMessage(message);
      return { id: response?.id, status: meta?.response?.status };
    },
    transformErrorResponse: (response: any, meta: any): any => {
      const error = getBaseQueryError(response);

      if (
        showErrorToastMessage &&
        !isAbortError(response) &&
        !shouldSuppressGlobalErrorToast(response) &&
        !shouldSuppressErrorToast?.(response)
      ) {
        errorToastMessage(error?.message);
      }

      return { message: error?.message, status: meta?.response?.status };
    },
  };
};

const ABORT_ERROR_REGEX =
  /(aborterror|aborted request|user aborted a request|request was aborted|signal is aborted|aborted)/i;

const getAbortCheckValues = (value: unknown, depth = 0): string[] => {
  if (depth > 3 || value == null) {
    return [];
  }
  if (typeof value === 'string') {
    return [value];
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }
  if (value instanceof Error) {
    return [
      value.name || '',
      value.message || '',
      ...getAbortCheckValues(
        (value as Error & { cause?: unknown }).cause,
        depth + 1,
      ),
    ];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => getAbortCheckValues(item, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap((item) =>
      getAbortCheckValues(item, depth + 1),
    );
  }
  return [];
};

export const isFetchAbortSerializedError = (response: {
  status?: string | number;
  error?: unknown;
  data?: unknown;
}): boolean => {
  const valuesToCheck = getAbortCheckValues({
    status: response?.status,
    error: response?.error,
    data: response?.data,
  });

  return valuesToCheck.some((value) => ABORT_ERROR_REGEX.test(value));
};

const isAbortError = isFetchAbortSerializedError;

/** i18n key prefix for backend error codes (default namespace, nested key). */
const BE_ERRORS_KEY_PREFIX = 'BE_errors.';

interface ITransformedErrorResponseWithNotificationOptions {
  shouldSuppressErrorToast?: (error: unknown) => boolean;
}

export const TransformedErrorResponseWithNotification = (
  options: ITransformedErrorResponseWithNotificationOptions = {},
) => {
  const { shouldSuppressErrorToast } = options;
  return {
    transformErrorResponse: (response: any, meta: any) => {
      const error = getBaseQueryError(response);

      if (
        !isAbortError(response) &&
        !shouldSuppressGlobalErrorToast(response) &&
        !shouldSuppressErrorToast?.(response)
      ) {
        const displayMessage =
          error?.code &&
          typeof error.code === 'string' &&
          i18n.exists(BE_ERRORS_KEY_PREFIX + error.code)
            ? i18n.t(BE_ERRORS_KEY_PREFIX + error.code)
            : error?.message;
        errorToastMessage(displayMessage);
      }
      return error;
    },
  };
};

export const getError = (e) => {
  const errors = e?.response?.data?.errors;
  return errors ? Object.values(errors).join(' ') : Messages.someWrong;
};

export const getErrorFromResponse = (resp: {
  status?: number | string;
  data?: ApiErrorBody | { errors?: Record<string, string[]> } | unknown;
  error?: unknown;
}): string => {
  const data = resp?.data;
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const body = data as ApiErrorBody;
    if (typeof body.message === 'string') return body.message;
  }
  const errors = (data as { errors?: Record<string, string[]> } | undefined)
    ?.errors;
  if (errors) {
    return Object.values(errors).join(' ');
  }
  if (resp.status === 404) {
    return Messages.notFound;
  }
  if (resp.status === 403) {
    return Messages.accessDenied;
  }
  // Surface real error for network/fetch failures (CORS, DNS, connection, etc.)
  if (
    resp?.status === 'FETCH_ERROR' ||
    resp?.status === 'PARSING_ERROR' ||
    resp?.status === 'TIMEOUT_ERROR'
  ) {
    const rawMessage = resp?.error ?? resp?.data;
    const message =
      typeof rawMessage === 'string'
        ? rawMessage
        : String(rawMessage || '').slice(0, 200);
    if (message) {
      if (process.env.REACT_APP_ENVIRONMENT !== 'prod') {
        console.error('[API fetch error]', resp?.status, resp?.error ?? resp);
      }
      return message;
    }
  }

  return Messages.someWrong;
};

export const getBaseQueryError = (resp: {
  status?: number;
  data?: ApiErrorBody | { errors?: Record<string, string[]> } | unknown;
  error?: unknown;
}): CommonError => {
  const data = resp?.data;
  const code =
    data &&
    typeof data === 'object' &&
    'code' in data &&
    typeof (data as ApiErrorBody).code === 'string'
      ? (data as ApiErrorBody).code
      : undefined;
  return {
    message: getErrorFromResponse(resp),
    status: resp.status ?? 0,
    ...(code ? { code } : {}),
  };
};

export const getListProvidesTags = (data, tag, deepFieldName = '') => {
  return data
    ? [
        ...data.map((item) => ({
          type: tag,
          id: deepFieldName ? item[deepFieldName].id : item.id,
        })),
        { type: tag, id: REDUX_CACHE.LIST },
      ]
    : [{ type: tag, id: REDUX_CACHE.LIST }];
};

const BASE_URL = config.apiBaseUrl;

export const callApiEndpoint = (uri) => BASE_URL + uri;

function getUpdatedBaseQueryArgs(args: string | FetchArgs): string | FetchArgs {
  if (typeof args === 'string') {
    return {
      url: getUrlWithLanguage(args),
      keepalive: true,
    };
  }

  return {
    ...args,
    url: getUrlWithLanguage(args.url),
    keepalive: args.keepalive ?? true,
  };
}

export function getBaseQuery(uri = '') {
  const baseQuery = fetchBaseQuery({
    baseUrl: callApiEndpoint(uri),
    prepareHeaders: getPrepareHeaders(),
  }) as BaseQueryFnType;

  return async (args, api, extraOptions) => {
    const updatedArgs = getUpdatedBaseQueryArgs(args);
    let result = await baseQuery(updatedArgs, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const refreshedToken = await authTokenHolder.getToken();
      if (refreshedToken) {
        result = await baseQuery(updatedArgs, api, extraOptions);
      }
    }

    if (result.error && result.error.status === 599) {
      api.dispatch(enableMaintenanceMode());
    } else if (result.error && result.error.status === 529) {
      api.dispatch(enableServiceUnavailableMode());
    }

    return result;
  };
}

export function getPrepareHeaders() {
  return async (headers: Headers) => {
    const token = await authTokenHolder.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  };
}

export function getUrlWithLanguage(urlDefault) {
  let url = urlDefault;
  const language = LocalStorageService.getValue(LocalStorageKey.language);

  if (url.indexOf('?') !== -1) {
    url = `${url}&language=${language}`;
  } else {
    url = `${url}?language=${language}`;
  }

  return url;
}

export function getUrlParamsString(params: { [key: string]: string | number }) {
  return Object.entries(params)
    .filter(([key, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}
